import {
    Cart,
    CartEndpoint as CartEndpointModel,
    BookingOrigin,
    ProductType,
    ActivityProduct,
    ActivityTicket,
    ActivityTicketSpecificField,
} from './cart.interfaces';
import { delay } from '@shared/utils/async';
import { HttpEndpoint } from '@/api/httpEndpoints';
import { AnyProduct } from '@shared/interfaces/product';
import { isMainProduct } from './cart.helpers';
import { fakeCartData, generateAddons } from '@/api/Cart/cart.fake';
import { isSpecialRequirementsField } from '@shared/helpers/Cart/Activity';
import { isCheckoutPage } from '@/helpers/url';
import fakeCartJSON from '@/api/Cart/cart.fake.json';

export class CartEndpoint extends HttpEndpoint implements CartEndpointModel {
    public constructor(applicationUrl: string) {
        super(applicationUrl);
    }

    public async GetCart(bookingToken: string): Promise<Cart> {
        const response = await this.get<Cart>(
            this.buildPathWithQuery('mvc/Booking/GetCartAndOptionsForBooking', {
                bookingToken,
                excludeCertainProductsFromParentPrice: 'true',
            }),
            { withCredentials: true }
        );

        return response;
    }

    public async UpdateCart(
        cart: Cart,
        origin: BookingOrigin,
        moveToNextStage: boolean,
        validateMandatory = false
    ): Promise<Cart> {
        return await this.post('mvc/Booking/UpdateCartAndOptionsForBooking', {
            serialisedCartAndOptionsForBooking: getSerializedCart(cart),
            origin,
            moveToNextStage,
            validateMandatory,
        });
    }
}

export class FakeCartEndpoint implements CartEndpointModel {
    public async GetCart(): Promise<Cart> {
        await delay(1500);

        const hasJSON = fakeCartJSON && Object.keys(fakeCartJSON).length > 0;

        if (hasJSON) {
            return fakeCartJSON as any;
        } else {
            return this.FilterResponse(fakeCartData as Cart);
        }
    }

    public async UpdateCart(
        cart: Cart,
        origin: BookingOrigin,
        moveToNextStage: boolean
    ): Promise<Cart> {
        const response = { ...cart };
        const mainProducts = response.Products.filter((p) => isMainProduct(p.Type));

        await delay(1000);

        if (moveToNextStage) {
            if (cart.FlowContainsPostCheckout && isCheckoutPage()) {
                response.NextUrl = '/travel/post-checkout';
            } else {
                response.NextUrl = '/travel/payment';
            }
        } else if (mainProducts.length === 0) {
            response.NextUrl = 'https://www.sembo.se/d/booking/survey.aspx';
        } else {
            response.NextUrl = '';
        }

        return this.FilterResponse(response);
    }

    private FilterResponse(response: Cart): Cart {
        const addons = generateAddons(response.Products);

        return {
            ...response,
            AvailableAddons: addons,
        };
    }
}

function getSerializedCart(cart: Cart): string {
    const mappedCart: Cart = {
        ...cart,
        Products: cart.Products.map(mapProduct),
    };

    return JSON.stringify(mappedCart);
}

function mapProduct(product: AnyProduct): AnyProduct {
    if (product.Type === ProductType.Activity) {
        const activity = product as ActivityProduct;

        return {
            ...activity,
            Tickets: mapTickets(activity.Tickets),
        };
    }

    return product;
}

function mapTickets(tickets: ActivityTicket[]): ActivityTicket[] {
    return tickets.map((ticket) => ({
        ...ticket,
        Pax: {
            ...ticket.Pax,
            TicketFields: ticket.Pax.TicketFields.map(setDefaultAnswer),
        },
    }));
}

function setDefaultAnswer(field: ActivityTicketSpecificField): ActivityTicketSpecificField {
    if (
        isSpecialRequirementsField(field.Id) &&
        (!field.Answer || field.Answer.length === 0)
    ) {
        return { ...field, Answer: ['No'] };
    }

    return field;
}
